import React, { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../../components/Layout";
import {
  faSort,
  faSortUp,
  faSortDown,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ConfirmModal from "../../components/ConfirmModal";
import SuccessModal from "../../components/SuccessModal";
import ErrorModal from "../../components/ErrorModal";

type User = {
  id: number;
  employeeId: string;
  name: string;
  nameEn: string;
  departmentName: string;
  isSales: boolean;
};

const ViewingPermissionSetting: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false); // ローディング状態
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false); // 確認モーダル
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false); // 完了モーダル
  const [isErrorModalOpen, setErrorModalOpen] = useState(false); // エラーモーダル
  const [sortConfig, setSortConfig] = useState<{
    key: keyof User;
    direction: "asc" | "desc" | null;
  }>({ key: "id", direction: null });

  // ユーザー取得処理
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get<User[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/tf/sales-setting/users`,
        { withCredentials: true },
      );
      const sortedUserData = [...response.data].sort((a, b) => {
        if (a.employeeId == null) return 1;
        if (b.employeeId == null) return -1;
        return String(a.employeeId).localeCompare(String(b.employeeId));
      });
      setUsers(sortedUserData);
    } catch (error) {
      console.error("ユーザー取得エラー:", error);
      setErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "スキルマネジメント - 営業設定";
    fetchUsers();
  }, []);

  // 個別のステータス切り替え
  const toggleMemberStatus = (id: number) => {
    setUsers(
      users.map((user) =>
        user.id === id ? { ...user, isSales: !user.isSales } : user,
      ),
    );
  };

  // 全選択切り替え
  const toggleAllMembers = () => {
    const allSelected = users.every((user) => user.isSales);
    setUsers(users.map((user) => ({ ...user, isSales: !allSelected })));
  };

  // 保存ボタン押下時
  const saveRoles = () => {
    setConfirmModalOpen(true); // 確認モーダル表示
  };

  // 確認モーダルの「OK」処理
  const handleConfirm = async () => {
    setConfirmModalOpen(false); // 確認モーダル非表示
    try {
      setLoading(true);
      const selectedUserIds = users
        .filter((user) => user.isSales)
        .map((user) => user.id);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/tf/sales-setting/users/update-roles`,
        { ids: selectedUserIds },
        { withCredentials: true },
      );
      // データ再取得
      await fetchUsers();
      setSuccessModalOpen(true); // 成功モーダル表示
    } catch (error) {
      console.error("保存エラー:", error);
      setErrorModalOpen(true); // エラーモーダル表示
    } finally {
      setLoading(false);
    }
  };

  // 確認モーダルのキャンセル処理
  const handleCancel = () => {
    setConfirmModalOpen(false);
  };

  // 完了モーダルの閉じる処理
  const handleSuccessClose = () => {
    setSuccessModalOpen(false);
  };

  // エラーモーダルの閉じる処理
  const handleErrorClose = () => {
    setErrorModalOpen(false);
  };

  const requestSort = (key: keyof User) => {
    let direction: "asc" | "desc" | null = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: keyof User) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? faSortUp : faSortDown;
    }
    return faSort;
  };

  const sortedData = [...users].sort((a, b) => {
    if (sortConfig.direction === null) return 0;
    let [aValue, bValue] = [a[sortConfig.key], b[sortConfig.key]];

    const aIsEmpty = aValue == null || aValue === "";
    const bIsEmpty = bValue == null || bValue === "";

    if (aIsEmpty && bIsEmpty) return 0; // 両方空なら順序維持
    if (aIsEmpty) return sortConfig.direction === "asc" ? 1 : -1;
    if (bIsEmpty) return sortConfig.direction === "asc" ? -1 : 1;

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <Layout>
      <div className="p-10 bg-white rounded-3xl min-h-[85vh]">
        {loading ? (
          // ローディング中のスケルトン
          <>
            <div className="flex mb-4">
              <div className="border-l-8 rounded-3xl border-slate-100 mr-4"></div>
              <div className="w-48 h-6 bg-slate-200 rounded animate-pulse"></div>
              <div className="text-lg text-transparent select-none">
                レイアウトの為の透明文字
              </div>
            </div>
            <div className="overflow-auto border-2 border-slate-300 bg-slate-100 rounded-2xl">
              <div className="p-6 space-y-4 min-w-full">
                {Array.from({ length: 10 }).map((_, index) => (
                  <div key={index} className="flex space-x-4">
                    <div className="w-8 h-8 mx-20 bg-slate-200 rounded animate-pulse"></div>
                    <div className="flex-1 h-8 bg-slate-200 rounded animate-pulse"></div>
                    <div className="flex-1 h-8 bg-slate-200 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex mb-4">
              <div className="title-text font-black">基本設定</div>
            </div>
            <div className="flex items-center justify-end mb-4">
              <div className="text-2xl text-iso font-bold mr-2">
                {users.filter((user) => user.isSales).length}
              </div>
              <div className="mt-2">件選択中：</div>
              <button
                onClick={saveRoles}
                className="bg-iso text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50"
              >
                <FontAwesomeIcon icon={faCircleCheck} className="mr-2" />
                保存
              </button>
            </div>
            <div className="overflow-auto max-h-[70vh] border-2 border-slate-300 bg-slate-100 rounded-2xl">
              <table className="min-w-full text-slate-600 border-collapse table-auto">
                <thead className="sticky top-0 bg-slate-100 border-b-2 border-slate-300 z-10">
                  <tr>
                    <th className="py-2 px-4 text-center cursor-pointer">
                      <div className="flex items-center justify-center space-x-2">
                        <input
                          type="checkbox"
                          onChange={toggleAllMembers}
                          checked={users.every((user) => user.isSales)}
                          className="appearance-none w-6 h-6 border-2 border-slate-300 rounded-md cursor-pointer checked:bg-iso checked:border-iso"
                        />
                        <span>共同編集権限</span>
                      </div>
                    </th>
                    {[
                      { key: "employeeId", label: "社員番号" },
                      { key: "name", label: "氏名" },
                      { key: "nameEn", label: "氏名（英）" },
                      { key: "departmentName", label: "所属" },
                    ].map((column) => (
                      <th
                        key={column.key}
                        onClick={() => requestSort(column.key as keyof User)}
                        className="py-2 px-4 text-center"
                      >
                        <div className="flex items-center justify-center">
                          {column.label}
                          <FontAwesomeIcon
                            icon={getSortIcon(column.key as keyof User)}
                            className="ml-1"
                          />
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((user, index) => (
                    <tr
                      key={user.id}
                      className={`${
                        index % 2 === 0 ? "bg-white" : "bg-slate-50"
                      } hover:bg-[#E4F4F7] cursor-pointer`}
                      onClick={() => toggleMemberStatus(user.id)}
                    >
                      <td className="py-2 px-4 text-center">
                        <div className="relative inline-block">
                          <input
                            type="checkbox"
                            checked={user.isSales}
                            readOnly
                            className="appearance-none w-6 h-6 border-2 border-slate-300 rounded-md cursor-pointer checked:bg-iso checked:border-iso"
                          />
                          {user.isSales && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <img
                                src="/check-icon.svg"
                                alt="Check"
                                className="w-4 h-4"
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-4 text-center">
                        {user.employeeId}
                      </td>
                      <td className="py-2 px-4 text-center">{user.name}</td>
                      <td className="py-2 px-4 text-center">{user.nameEn}</td>
                      <td className="py-2 px-4 text-center">
                        {user.departmentName}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* 確認モーダル */}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        title="保存確認"
        text={
          "変更を保存しますか？\nチェックしたユーザは全ユーザーのスキルシートを閲覧・編集可能になります。"
        }
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {/* 完了モーダル */}
      <SuccessModal
        isOpen={isSuccessModalOpen}
        title="保存成功"
        text="設定が保存されました。"
        onClose={handleSuccessClose}
      />

      {/* エラーモーダル */}
      <ErrorModal
        isOpen={isErrorModalOpen}
        title="保存エラー"
        text="保存中にエラーが発生しました。再度お試しください。"
        onClose={handleErrorClose}
      />
    </Layout>
  );
};

export default ViewingPermissionSetting;
